import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AgoraRTC from 'agora-rtc-sdk-ng';
import {
    Box, Typography, Grid, Card, CardContent,
    Button, Avatar, CircularProgress, IconButton
} from '@mui/material';
import { Fullscreen, CloseFullscreen } from "@mui/icons-material";

const ProctorDashboard = () => {
    const { examId } = useParams();
    const [remoteUsers, setRemoteUsers] = useState({});
    const [loading, setLoading] = useState(true);
    const clientRef = useRef(null);

    useEffect(() => {
        const initAgora = async () => {
            const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
            clientRef.current = client;

            try {
                await client.join(
                    '04e8b2a241274918a6e3a365cf5252c4',
                    `exam${examId}`,
                    null,
                    null
                );

                client.on('user-published', async (user, mediaType) => {
                    await client.subscribe(user, mediaType);

                    setRemoteUsers(prev => {
                        const updatedUser = {
                            ...prev[user.uid],
                            uid: user.uid,
                            ...(mediaType === 'video' && { hasVideo: true, videoTrack: user.videoTrack }),
                            ...(mediaType === 'audio' && { hasAudio: true, audioTrack: user.audioTrack }),
                        };
                        return { ...prev, [user.uid]: updatedUser };
                    });

                    // 🔥 DO NOT PLAY AUDIO HERE! 
                    // (We will play only when entering Fullscreen)
                });

                client.on('user-unpublished', (user, mediaType) => {
                    setRemoteUsers(prev => {
                        if (!prev[user.uid]) return prev;

                        return {
                            ...prev,
                            [user.uid]: {
                                ...prev[user.uid],
                                ...(mediaType === 'video' && { hasVideo: false, videoTrack: null }),
                                ...(mediaType === 'audio' && { hasAudio: false, audioTrack: null }),
                            }
                        };
                    });
                });

                setLoading(false);
            } catch (error) {
                console.error('Agora initialization failed:', error);
                setLoading(false);
            }
        };

        initAgora();

        return () => {
            if (clientRef.current) {
                clientRef.current.leave();
            }
        };
    }, [examId]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Live Proctoring Dashboard
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                Exam ID: {examId} | Connected Students: {Object.keys(remoteUsers).length}
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
                {Object.values(remoteUsers).map((user) => (
                    <Grid item xs={12} sm={6} md={4} key={user.uid}>
                        <StudentCard user={user} />
                    </Grid>
                ))}
            </Grid>

            {Object.keys(remoteUsers).length === 0 && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh'
                }}>
                    <Typography variant="h6" color="textSecondary">
                        No students connected yet
                    </Typography>
                </Box>
            )}
        </Box>
    );
};



const StudentCard = ({ user }) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [isFullScreen, setIsFullScreen] = useState(false);

    // Enter fullscreen using the container element
    const handleEnterFullScreen = async () => {
        if (containerRef.current) {
            try {
                if (containerRef.current.requestFullscreen) {
                    await containerRef.current.requestFullscreen();
                } else if (containerRef.current.webkitRequestFullscreen) { // Safari
                    await containerRef.current.webkitRequestFullscreen();
                } else if (containerRef.current.msRequestFullscreen) { // IE11
                    await containerRef.current.msRequestFullscreen();
                }
                // Play the student's audio after entering full screen if available
                if (user.audioTrack) {
                    user.audioTrack.play();
                }
            } catch (error) {
                console.error("Error entering fullscreen:", error);
            }
        }
    };

    // Exit fullscreen mode
    const handleExitFullScreen = async () => {
        try {
            if (document.fullscreenElement) {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    await document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) {
                    await document.msExitFullscreen();
                }
            }
            if (user.audioTrack) {
                user.audioTrack.stop();
            }
        } catch (error) {
            console.error("Error exiting fullscreen:", error);
        }
    };

    // Listen for fullscreen change events to update the state
    useEffect(() => {
        const fullscreenChangeHandler = () => {
            if (document.fullscreenElement === containerRef.current) {
                setIsFullScreen(true);
            } else {
                setIsFullScreen(false);
            }
        };

        document.addEventListener("fullscreenchange", fullscreenChangeHandler);
        return () => {
            document.removeEventListener("fullscreenchange", fullscreenChangeHandler);
        };
    }, []);

    // Play the video track on mount and cleanup on unmount
    useEffect(() => {
        if (videoRef.current && user.videoTrack) {
            user.videoTrack.play(videoRef.current);
        }
        return () => {
            if (user.videoTrack) {
                user.videoTrack.stop();
            }
            if (user.audioTrack) {
                user.audioTrack.stop();
            }
        };
    }, [user.videoTrack, user.audioTrack]);

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Box sx={{ position: "relative", pt: "56.25%" }}>
                    {user.hasVideo ? (
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                backgroundColor: "#000"
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "#f5f5f5"
                            }}
                        >
                            <Avatar sx={{ width: 80, height: 80 }}>Student</Avatar>
                        </Box>
                    )}

                    {/* Render the custom exit fullscreen button when in fullscreen */}
                    {isFullScreen && (
                        <IconButton
                            sx={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                color: "#fff",
                                zIndex: 10000,
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.7)"
                                }
                            }}
                            onClick={handleExitFullScreen}
                        >
                            <CloseFullscreen />
                        </IconButton>
                    )}
                </Box>

                {/* Only show additional card content when NOT in fullscreen */}
                {!isFullScreen && (
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography gutterBottom variant="h6">
                                Student {user.uid}
                            </Typography>
                            <IconButton color="primary" onClick={handleEnterFullScreen} aria-label="View full screen">
                                <Fullscreen />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.secondary">
                                Status: {user.hasVideo ? "Active" : "Inactive"}
                            </Typography>
                            <Button size="small" variant="outlined" onClick={handleEnterFullScreen} sx={{ ml: 1 }}>
                                Full View
                            </Button>
                        </Box>
                    </CardContent>
                )}
            </Card>
        </div>
    );
};




export default ProctorDashboard;




